/** @app — application layer; owned per-fork (LMS / OTT). See PLATFORM_FILES.md. */
import type { LearningCourseDetail } from '@akademi/site-builder-runtime';

// SkillChips — categorized chip rows with color-coded categories.
//
// The schema's skills array has optional `category`. Skills are
// grouped by category and rendered as chips. Each category gets a
// stable color class (c0/c1/c2) hashed from the category string —
// so "DİL" is always the same color across the page and across
// reloads, even though we don't have to hard-code which categories
// exist.
//
// Three color slots is enough variety to differentiate adjacent
// categories without painting a rainbow. Any category that hashes
// to the same slot as another shares the same tint — that's fine,
// they're still visually grouped.

interface SkillChipsProps {
  skills: NonNullable<LearningCourseDetail['skills']>;
}

export function SkillChips({ skills }: SkillChipsProps) {
  if (skills.length === 0) return null;

  // Preserve original order — Map preserves insertion order, so
  // categories appear in the order they first appear in the data.
  const groups = new Map<string, string[]>();
  for (const s of skills) {
    const key = s.category ?? '_';
    if (!groups.has(key)) groups.set(key, []);
    groups.get(key)!.push(s.name);
  }

  return (
    <div className="cd-skills">
      {Array.from(groups.entries()).map(([cat, names]) => {
        const colorClass = cat === '_' ? '' : ` cd-skill-group-c${categoryColorIndex(cat)}`;
        return (
          <div key={cat} className={`cd-skill-group${colorClass}`}>
            {cat !== '_' && <span className="cd-skill-group-label">{cat}</span>}
            {names.map((n, i) => (
              <span key={i} className="cd-skill-chip">
                {n}
              </span>
            ))}
          </div>
        );
      })}
    </div>
  );
}

/**
 * Stable hash → 0, 1, or 2. Same category string always returns
 * the same slot. Uses a simple DJB2-ish multiplier so distinct
 * category strings distribute reasonably across the three slots.
 */
function categoryColorIndex(category: string): 0 | 1 | 2 {
  let h = 5381;
  for (let i = 0; i < category.length; i++) {
    h = ((h << 5) + h + category.charCodeAt(i)) | 0;
  }
  return (Math.abs(h) % 3) as 0 | 1 | 2;
}
